import { AnimationEvent, animate, state, style, transition, trigger } from '@angular/animations';
import { CommonModule, NgClass, NgFor, NgIf } from '@angular/common';
import {
  ChangeDetectorRef,
  Component,
  ElementRef,
  HostBinding,
  Input,
  OnDestroy,
  OnInit,
  ViewChild
} from '@angular/core';
import { NavigationEnd, Router, RouterLink, RouterLinkActive } from '@angular/router';
import { environment } from '@environments/environment';
import { TranslateModule } from '@ngx-translate/core';
import { AuthService } from '@shared/services/auth.service';
import { DomHandler } from 'primeng/dom';
import { RippleModule } from 'primeng/ripple';
import { TooltipModule } from 'primeng/tooltip';
import { Observable, Subject, Subscription, of } from 'rxjs';
import { filter, switchMap, takeUntil } from 'rxjs/operators';

import { MenuService } from './app.menu.service';
import { LayoutService } from './service/app.layout.service';

@Component({
  selector: '[app-menuitem]',
  templateUrl: 'app.menuitem.component.html',
  // eslint-disable-next-line @angular-eslint/component-max-inline-declarations
  animations: [
    trigger('children', [
      state(
        'collapsed',
        style({
          height: '0'
        })
      ),
      state(
        'expanded',
        style({
          height: '*'
        })
      ),
      state(
        'hidden',
        style({
          display: 'none'
        })
      ),
      state(
        'visible',
        style({
          display: 'block'
        })
      ),
      transition('collapsed <=> expanded', animate('400ms cubic-bezier(0.86, 0, 0.07, 1)'))
    ])
  ],
  imports: [
    NgIf,
    RippleModule,
    NgClass,
    TooltipModule,
    RouterLinkActive,
    RouterLink,
    NgFor,
    CommonModule,
    TranslateModule
  ]
})
export class AppMenuitemComponent implements OnInit, OnDestroy {
  @Input() item: any;

  @Input() index!: number;

  // @Input() menuActiveKey: string;

  @Input() @HostBinding('class.layout-root-menuitem') root!: boolean;

  @Input() parentKey!: string;

  @ViewChild('submenu') submenu!: ElementRef;

  menuActiveKey = null;

  active = false;

  menuSourceSubscription: Subscription;

  menuResetSubscription: Subscription;

  key: string = '';

  submenuActive = false;

  public onComponentDestroy$ = new Subject<void>();

  submenuAnimationState$ = new Observable();

  constructor(
    public layoutService: LayoutService,
    private cd: ChangeDetectorRef,
    public router: Router,
    private menuService: MenuService,
    private authService: AuthService
  ) {
    this.menuSourceSubscription = this.menuService.menuSource$.subscribe((value) => {
      Promise.resolve(null).then(() => {
        if (value.routeEvent) {
          this.active = value.key === this.key || value.key.startsWith(this.key + '-') ? true : false;
        }

        // else {
        //   if (value.key !== this.key && !value.key.startsWith(this.key + '-')) {
        //     this.active = false;
        //   }
        // }
      });
    });

    this.menuResetSubscription = this.menuService.resetSource$.subscribe(() => {
      // this.active = false;
      this.submenuActive = false;
    });

    this.router.events
      .pipe(
        filter((event) => event instanceof NavigationEnd),
        takeUntil(this.onComponentDestroy$)
      )
      .subscribe((_) => {
        if (this.isHorizontal) {
          this.active = false;
        } else {
          if (this.item.routerLink) {
            this.updateActiveStateFromRoute();
          }
        }
      });

    this.submenuAnimationState$ = this.menuService.expandSubmenu$.pipe(
      switchMap(() => of(this.submenuAnimation())),
      takeUntil(this.onComponentDestroy$)
    );
  }

  ngOnInit() {
    this.key = this.parentKey ? this.parentKey + '-' + this.index : String(this.index);

    if (this.item.routerLink) {
      this.updateActiveStateFromRoute();
    }

    this.layoutService.menuActiveKey$.pipe(takeUntil(this.onComponentDestroy$)).subscribe((val) => {
      if (this.menuActiveKey !== val) {
        return (this.menuActiveKey = val);
      } else {
        return (this.menuActiveKey = null);
      }
    });

    this.layoutService.configUpdate$.pipe(takeUntil(this.onComponentDestroy$)).subscribe(() => {
      // this.layoutService.updatedMenuActiveKey(null);

      if (this.root && this.active && this.layoutService.isHorizontal()) {
        this.calculatePosition(this.submenu?.nativeElement, this.submenu?.nativeElement.parentElement);
      }
    });
  }

  updateActiveStateFromRoute() {
    const activeRoute = this.router.isActive(this.item.routerLink[0], {
      paths: 'subset',
      queryParams: 'ignored',
      matrixParams: 'ignored',
      fragment: 'ignored'
    });

    if (activeRoute) {
      this.menuService.onMenuStateChange({ key: this.key, routeEvent: true });

      setTimeout(() => {
        this.layoutService.updateTopbarTitle(this.item.title);
      }, 0);
    }
  }

  onSubmenuAnimated(event: AnimationEvent) {
    if (event.toState === 'visible' && this.layoutService.isHorizontal()) {
      const el = <HTMLUListElement>event.element;
      const elParent = <HTMLUListElement>el.parentElement;
      this.calculatePosition(el, elParent);
    }
  }

  calculatePosition(overlay: HTMLElement, target: HTMLElement): void {
    if (overlay) {
      const { left } = target.getBoundingClientRect();
      const [vWidth] = [window.innerWidth, window.innerHeight];
      const [oWidth] = [overlay.offsetWidth, overlay.offsetHeight];
      const scrollbarWidth = DomHandler.calculateScrollbarWidth();
      // reset
      overlay.style.top = '';
      overlay.style.left = '';

      if (this.layoutService.isHorizontal()) {
        const width = left + oWidth + scrollbarWidth;
        overlay.style.left = vWidth < width ? `${left - (width - vWidth)}px` : `${left}px`;
      }
    }
  }

  itemClick(event: Event) {
    // avoid processing disabled items
    if (this.item.disabled) {
      event.preventDefault();
      return;
    }

    // navigate with hover
    if (this.root || this.isHorizontal) {
      this.layoutService.state.menuHoverActive = !this.layoutService.state.menuHoverActive;
    }

    // execute command
    if (this.item.command) {
      this.item.command({ originalEvent: event, item: this.item });
    }

    // toggle active state
    if (this.item.items) {
      this.submenuActive = !this.submenuActive;

      if (this.root && this.submenuActive && this.isHorizontal) {
        this.layoutService.onOverlaySubmenuOpen();
      }
    } else {
      if (this.layoutService.isMobile() || this.layoutService.isTablet()) {
        this.layoutService.onMenuToggle();
        this.layoutService.config.update((config) => ({
          ...config,
          menuMode: config.menuMode === 'static' ? 'slim' : 'static'
        }));

        if (this.layoutService.isMobile()) {
          this.layoutService.state.staticMenuMobileActive = false;
        }
      }

      if (this.isHorizontal) {
        this.menuService.reset();
        this.layoutService.state.menuHoverActive = false;
      }
    }

    if (this.key.split('-').length <= 2) {
      this.layoutService.updatedMenuActiveKey(this.key);
      this.menuService.onMenuStateChange({ key: this.key });
      this.menuService.onExpandSubmenu();
    } else {
      this.authService.getWebVersion().subscribe((version) => {
        const currentVersion = environment.version.split('-')[0];
        if (version && currentVersion && currentVersion !== version) {
          event.preventDefault();
          const path = this.item.routerLink[0].replace(/^\/+/, '');

          const url = this.router.serializeUrl(
            this.router.createUrlTree([path], {
              queryParams: this.item.queryParams,
              fragment: this.item.fragment,
              queryParamsHandling: this.item.queryParamsHandling,
              preserveFragment: this.item.preserveFragment
            })
          );
          window.location.href = url;
        }

        this.menuService.onMenuStateChange({ key: this.key });
        this.menuService.onExpandSubmenu();
      });
    }
  }

  submenuAnimation(): 'visible' | 'hidden' | 'expanded' | 'collapsed' {
    if (this.layoutService.isHorizontal()) {
      return this.key == this.menuActiveKey ? 'visible' : 'hidden';
    } else {
      if (this.root) {
        return 'expanded';
      } else if (this.key == this.menuActiveKey) {
        return 'expanded';
      } else {
        return 'collapsed';
      }
    }
  }

  get isHorizontal(): boolean {
    return this.layoutService.isHorizontal();
  }

  @HostBinding('class.active-menuitem')
  get activeClass() {
    return this.active;
  }

  ngOnDestroy() {
    if (this.menuSourceSubscription) {
      this.menuSourceSubscription.unsubscribe();
    }

    if (this.menuResetSubscription) {
      this.menuResetSubscription.unsubscribe();
    }

    this.onComponentDestroy$.next();
    this.onComponentDestroy$.complete();
  }
}
